/**
 * Mode degrade et file d'attente hors ligne | Offline support.
 *
 * Les agents des centres eloignes travaillent souvent sur une connexion
 * intermittente. La progression enregistree pendant une coupure est mise en
 * file d'attente dans le navigateur, puis rejouee des le retour du reseau.
 */

const DB_NAME = 'dgi-offline';
const STORE = 'outbox';
export const OUTBOX_CHANGED_EVENT = 'dgi:outbox-changed';

export interface QueuedRequest {
  id?: number;
  path: string;
  method: 'POST' | 'PUT' | 'PATCH';
  body: unknown;
  queuedAt: number;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStore<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = action(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => db.close();
      }),
  );
}

const notify = (): void => {
  window.dispatchEvent(new CustomEvent(OUTBOX_CHANGED_EVENT));
};

/** Met une requete en attente ; un echec de stockage n'interrompt jamais l'agent. */
export async function enqueue(entry: Omit<QueuedRequest, 'id' | 'queuedAt'>): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.add({ ...entry, queuedAt: Date.now() }));
    notify();
  } catch {
    /* IndexedDB indisponible : la requete est simplement perdue */
  }
}

export async function pendingCount(): Promise<number> {
  try {
    return await withStore<number>('readonly', (store) => store.count());
  } catch {
    return 0;
  }
}

async function listQueued(): Promise<QueuedRequest[]> {
  try {
    return await withStore<QueuedRequest[]>('readonly', (store) => store.getAll() as IDBRequest<QueuedRequest[]>);
  } catch {
    return [];
  }
}

async function remove(id: number): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.delete(id) as unknown as IDBRequest<undefined>);
  } catch {
    /* rien a faire : l'entree sera rejouee au prochain passage */
  }
}

/**
 * Rejoue la file d'attente. Une requete qui echoue de nouveau est conservee ;
 * une requete refusee par le serveur (4xx) est abandonnee, car la rejouer
 * donnerait le meme refus.
 */
export async function flushOutbox(send: (entry: QueuedRequest) => Promise<Response>): Promise<number> {
  const entries = await listQueued();
  let replayed = 0;

  for (const entry of entries) {
    try {
      const response = await send(entry);
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        if (entry.id !== undefined) await remove(entry.id);
        if (response.ok) replayed += 1;
      }
    } catch {
      // Toujours hors ligne : on garde la file pour le prochain essai.
      break;
    }
  }

  if (replayed > 0) notify();
  return replayed;
}

/** Enregistre le service worker, en production uniquement. */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      /* l'absence de service worker degrade l'experience, sans la bloquer */
    });
  });
}
